import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { getDaysInMonth, isMatch } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

import Select from './Select';

import { BorderRadius, Colors, FontSizes } from '../Theme';

const DatePicker = ({ future, hidePlaceholders, invalid, onChange, readOnly, required, value }) => {
  const styles = {
    component: {
      display: 'flex',
      position: 'relative'
    },
    required: {
      color: Colors.RED.hex,
      position: 'absolute',
      right: 5,
      top: 5,
      lineHeight: 1,
      fontSize: FontSizes.LARGE
    }
  };
  const [month, setMonth] = useState('');
  const [day, setDay] = useState('');
  const [year, setYear] = useState('');

  useEffect(() => {
    let date;

    if (value && isMatch(value, 'yyyy-MM-dd')) {
      date = formatInTimeZone(value, 'UTC', 'yyyy-MM-dd HH:mm:ss');
    } else date = value ? new Date(value) : Date.now();

    setMonth(formatInTimeZone(date, 'America/Denver', 'MM'));
    setDay(formatInTimeZone(date, 'America/Denver', 'dd'));
    setYear(formatInTimeZone(date, 'America/Denver', 'yyyy'));
  }, [value]);

  const generateMonths = () => {
    const options = hidePlaceholders ? [] : [{ value: '', label: '--' }];

    return options.concat([
      { value: '01', label: 'January' },
      { value: '02', label: 'February' },
      { value: '03', label: 'March' },
      { value: '04', label: 'April' },
      { value: '05', label: 'May' },
      { value: '06', label: 'June' },
      { value: '07', label: 'July' },
      { value: '08', label: 'August' },
      { value: '09', label: 'September' },
      { value: '10', label: 'October' },
      { value: '11', label: 'November' },
      { value: '12', label: 'December' }
    ]);
  };

  const generateDays = () => {
    const days = [];
    const num_days = getDaysInMonth(new Date(`${year || '3000'}-${month}-01 MST`));
    const options = hidePlaceholders ? [] : [{ value: '', label: '--' }];

    for (let i = 1; i <= num_days; i++) {
      days.push(i);
    }

    return options.concat(
      days.map(d => {
        return {
          value: d < 10 ? `0${d}` : `${d}`,
          label: d
        };
      })
    );
  };

  const generateYears = () => {
    const years = [];
    const this_year = parseInt(formatInTimeZone(new Date(), 'America/Denver', 'yyyy'), 10);
    const options = hidePlaceholders ? [] : [{ value: '', label: '--' }];

    if (future) {
      for (let i = this_year; i <= this_year + 10; i++) {
        years.push(i);
      }
    } else {
      for (let i = this_year - 100; i <= this_year; i++) {
        years.push(i);
      }
    }

    return options.concat(years.map(y => ({ value: `${y}` })));
  };

  const handleMonthChange = e => {
    const num_days = getDaysInMonth(new Date(`${year || '3000'}-${e.target.value}-01 MST`));
    const new_day = !e.target.value || parseInt(day, 10) > num_days ? '01' : day;

    setMonth(e.target.value);
    setDay(new_day);

    const date = new Date(`${year}-${e.target.value}-${new_day} MST`);

    onChange(date.toISOString());
  };

  const handleDayChange = e => {
    setDay(e.target.value);

    const date = new Date(`${year}-${month}-${e.target.value} MST`);

    onChange(date.toISOString());
  };

  const handleYearChange = e => {
    setYear(e.target.value);

    const date = new Date(`${e.target.value}-${month}-${day} MST`);

    onChange(date.toISOString());
  };

  return (
    <div style={styles.component}>
      <Select
        invalid={invalid}
        onChange={handleMonthChange}
        options={generateMonths()}
        readOnly={readOnly}
        style={{
          marginRight: -1,
          borderRadius: `${BorderRadius.DEFAULT}px 0px 0px ${BorderRadius.DEFAULT}px`,
          minWidth: 110,
          flex: 1
        }}
        value={month}
      />
      <Select
        invalid={invalid}
        onChange={handleDayChange}
        options={generateDays()}
        readOnly={readOnly}
        style={{ marginRight: -1, borderRadius: 0, minWidth: 50, flex: 1 }}
        value={day}
      />
      <Select
        invalid={invalid}
        onChange={handleYearChange}
        options={generateYears()}
        readOnly={readOnly}
        style={{
          marginRight: -1,
          borderRadius: `0px ${BorderRadius.DEFAULT}px ${BorderRadius.DEFAULT}px 0px`,
          minWidth: 75,
          flex: 1
        }}
        value={year}
      />
      {required ? <span style={styles.required}>*</span> : null}
    </div>
  );
};

DatePicker.propTypes = {
  future: PropTypes.bool,
  hidePlaceholders: PropTypes.bool,
  invalid: PropTypes.bool,
  onChange: PropTypes.func,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  value: PropTypes.string
};

export default DatePicker;
